<template>
  <sc-page-base
    title="Logs de Usuário">
    <sc-grid
      :loading="loading"
      :data="currentLogsList"
      :total="totalLogsUser"
      default-sort-column="action_time"
      default-sort-direction="desc"
      :has-backend-pagination="true"
      :has-backend-sorting="false"
      :default-filters="defaultFilters"
      :rules="rules"
      @pagination="searchLogsUser"
      @submit="searchLogsUser"
    >
      <template
        slot="filters"
        slot-scope="{ filters }">
        <sc-logs-user-filters
          :filters="filters"
          @change-filters="changeFilters" />
      </template>

      <template
        slot="filters-actions"
        slot-scope="{ isLoading, isValid, filters }">

        <sc-button
          :loading="exportLoading"
          :disabled="exportDisabled(isValid)"
          text="Exportar em Excel"
          class="button is-primary"
          @click="exportLogsUser({ filters })" />

        <sc-button
          type="submit"
          theme="primary"
          text="Buscar"
          :loading="isLoading" />
      </template>

      <template
        slot="before-table">
        <div class="buttons is-right">
          <sc-tooltip
            label="Escolha dois registros e compare as diferenças campo a campo"
            position="is-left"
            :delay="600"
            multilined>
            <sc-button
              theme="primary"
              text="Comparar detalhes"
              :disabled="diffDisabled"
              @click="openDiffModal"/>
            </sc-tooltip>
        </div>
      </template>

      <template slot="table-columns" slot-scope="{ props }">
        <sc-table-column
          label="Date e hora"
          field="action_time"
          width="160"
          sortable>
          {{ props.row.action_time }}
        </sc-table-column>

        <sc-table-column
          label="Autor"
          field="author_email"
          sortable>
          {{ props.row.author_email }}
        </sc-table-column>

        <sc-table-column
          label="Ação"
          field="action"
          sortable>
          {{ props.row.action }}
        </sc-table-column>

        <sc-table-column
          label="Origem"
          field="module"
          sortable>
          {{ props.row.module }}
        </sc-table-column>

        <sc-table-column
          label="Status"
          field="status"
          sortable>
          {{ props.row.status }}
        </sc-table-column>

        <sc-table-column
          label="O que mudou?"
          field="what_has_changed"
          sortable>
          {{ props.row.what_has_changed }}
        </sc-table-column>

        <sc-table-column
          label="Identificador"
          field="identity_change"
          sortable>
          {{ props.row.identity_change }}
        </sc-table-column>

        <sc-table-column
          label="Ver mais">
          <sc-button
            theme="primary"
            @click="() => openViewModal(props.row)">
            <sc-icon icon="plus" />
          </sc-button>
        </sc-table-column>

        <sc-table-column
          label="Comparar"
          field="isRowChecked">
          <sc-checkbox
            :value="props.row.isRowChecked"
            :disabled="props.row.isRowDisabled"
            @input="(isRowChecked) => changeCheckRow(props.row, isRowChecked)" />
        </sc-table-column>
      </template>
    </sc-grid>

    <sc-modal
      has-modal-card
      :active.sync="showViewUserLogModal">
      <sc-modal-card>
        <template slot="title">
          <h2 class="modal-card-title notification-title">
            Ver mais
          </h2>
        </template>
        <template slot="body">
          <div class="div-modal">
            <div
              v-if="errorMessage"
              class="content">
              <p><strong>Mensagem de erro: <span class="has-text-danger">{{ errorMessage }}</span></strong></p>
            </div>
            <div v-else>
              <div class="content">
                <p class="is-white">As linhas com fundo em branco indicam que não houve alterações.</p>
                <p class="is-red">As linhas com o sinal de menos (<span class="is-dark">-</span>) e fundo em vermelho indicam como os dados eram antes de serem alterados.</p>
                <p class="is-green">As linhas com o sinal de mais (<span class="is-dark">+</span>) e fundo em verde indicam como os dados ficaram após serem alterados.</p>
              </div>
              <sc-json-compare
                :oldData="userLogPayloadBefore"
                :newData="userLogPayloadAfter" />
            </div>
          </div>
        </template>
        <template slot="footer">
          <button class="button is-warning" @click="showViewUserLogModal = false">Fechar</button>
        </template>
      </sc-modal-card>
    </sc-modal>

    <sc-modal
      has-modal-card
      :active.sync="showCompareUserLogModal">
      <sc-modal-card>
        <template slot="title">
          <h2 class="modal-card-title notification-title">
            Comparar detalhes
          </h2>
        </template>
        <template slot="body">
          <div class="div-modal">
            <div class="content">
              <p class="is-white">As linhas com fundo em branco indicam que não houve alterações.</p>
              <p class="is-red">As linhas com o sinal de menos (<span class="is-dark">-</span>) e fundo em vermelho indicam como os dados eram antes de serem alterados.</p>
              <p class="is-green">As linhas com o sinal de mais (<span class="is-dark">+</span>) e fundo em verde indicam como os dados ficaram após serem alterados.</p>
            </div>
            <sc-json-compare :oldData="oldUserLog" :newData="newUserLog" />
          </div>
        </template>
        <template slot="footer">
          <button class="button is-warning" @click="showCompareUserLogModal = false">Fechar</button>
        </template>
      </sc-modal-card>
    </sc-modal>
  </sc-page-base>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  import {
    Moment,
    Lodash,
  } from '@intelipost-js/secure-providers';
  import {
    ScPageBase,
    ScGrid,
    ScButton,
    ScTableColumn,
    ScModal,
    ScModalCard,
    ScIcon,
    ScCheckbox,
    ScJsonCompare,
    ScTooltip,
  } from '@intelipost-js/secure-components';

  import ScLogsUserFilters from './components/sc-logs-user-filters/ScLogsUserFilters';

  const {
    mapState,
    mapActions,
    mapMutations,
} = createNamespacedHelpers('analytic/logsUser');

  export default {
    name: 'page-logs-user',
    data() {
      return {
        defaultFilters: {
          date_range: {
            start: this.defaultDate().subtract(7, 'days'),
            end: this.defaultDate(),
          },
        },
        filtersChanged: true,
        currentModule: null,
        currentLogsList: [],
        checkedLogsList: [],
        showCompareUserLogModal: false,
        showViewUserLogModal: false,
        newUserLog: null,
        oldUserLog: null,
        userLogPayloadBefore: null,
        userLogPayloadAfter: null,
        errorMessage: null,
        rules: {
          date_range: [{
            message: 'Informe o período de data',
            required: true,
          }],
        },
      };
    },
    computed: {
      ...mapState([
        'exportLoading',
        'loading',
        'logsUser',
        'totalLogsUser',
      ]),
      diffDisabled() {
        return this.checkedLogsList.length < 2;
      },
    },
    methods: {
      ...mapActions([
        'getLogsUser',
        'exportLogsUser',
      ]),
      ...mapMutations([
        'setLoading',
        'setLogsUser',
      ]),
      resetLogsUserData() {
        this.checkedLogsList = [];
        this.currentModule = null;
        this.filtersChanged = false;
      },
      defaultDate(currentTimestamp) {
        return Moment(currentTimestamp);
      },
      defaultStartDate(currentTimestamp) {
        return this.defaultDate(currentTimestamp).subtract(7, 'days').format('YYYY-MM-DD');
      },
      defaultEndDate(currentTimestamp) {
        return this.defaultDate(currentTimestamp).format('YYYY-MM-DD');
      },
      searchLogsUser(data) {
        this.resetLogsUserData();
        return this.getLogsUser(data)
          .then(() => {
            this.currentLogsList = Lodash.cloneDeep(this.logsUser);
            this.updateDisabledRows();
          });
      },
      changeFilters() {
        this.filtersChanged = true;
      },
      exportDisabled(isValid) {
        return !isValid || this.loading || this.filtersChanged || this.totalLogsUser < 1;
      },
      changeCheckRow(currentRow, isRowChecked) {
        const currentLogIndex = this.currentLogsList
          .findIndex(currentLog => currentLog.id === currentRow.id);
        this.$set(
          this.currentLogsList,
          currentLogIndex,
          Object.assign(currentRow, { isRowChecked }));

        this.checkedLogsList = this.currentLogsList.filter(currentLog => currentLog.isRowChecked);
        const checkedLogsListLength = this.checkedLogsList.length;

        if (checkedLogsListLength === 0) {
          this.currentModule = null;
        } else if (checkedLogsListLength === 1) {
          this.currentModule = currentRow.module;
        }

        this.updateDisabledRows();
      },
      updateDisabledRows() {
        this.currentLogsList = this.currentLogsList.map((currentLog) => {
          let isRowDisabled;
          if (this.diffDisabled) {
            isRowDisabled = currentLog.error_message ||
            (this.currentModule && this.currentModule !== currentLog.module);
          } else {
            isRowDisabled = !this.checkedLogsList
              .find(checkedItem => checkedItem.id === currentLog.id);
          }
          return Object.assign(currentLog, { isRowDisabled });
        });
      },
      openDiffModal() {
        if (!this.diffDisabled) {
          const diffListOrdered = this.checkedLogsList
            .sort((a, b) => {
              const aIsAfterB = Moment(a.action_time).isAfter(b.action_time) ? -1 : 1;
              return aIsAfterB;
            });
          this.newUserLog = JSON.parse(diffListOrdered[0].payload_after);
          this.oldUserLog = JSON.parse(diffListOrdered[1].payload_after);
          this.showCompareUserLogModal = true;
        }
      },
      openViewModal(row) {
        const {
          payload_after,
          payload_before,
          error_message,
        } = row;
        if (!error_message && payload_after && payload_before) {
          this.userLogPayloadBefore = JSON.parse(payload_before);
          this.userLogPayloadAfter = JSON.parse(payload_after);
        } else {
          this.userLogPayloadBefore = null;
          this.userLogPayloadAfter = null;
          this.errorMessage = error_message;
        }
        this.showViewUserLogModal = true;
      },
    },
    created() {
      this.setLoading(true);
      this.$store.dispatch('date/getCurrentDate')
      .then((currentTimestamp) => {
        this.defaultFilters.date_range = {
          start: this.defaultStartDate(currentTimestamp),
          end: this.defaultEndDate(currentTimestamp),
        };
        this.setLoading(false);
        this.searchLogsUser({
          filters: this.defaultFilters,
          pagination: {
            page: 1,
            limit: 10,
          },
        });
      });
    },
    components: {
      ScPageBase,
      ScGrid,
      ScLogsUserFilters,
      ScButton,
      ScTableColumn,
      ScModal,
      ScModalCard,
      ScIcon,
      ScCheckbox,
      ScJsonCompare,
      ScTooltip,
    },
  };
</script>

<style lang="scss" scoped>
  .div-modal {
    .content {
      text-align: center;
      margin: 0 auto;
      max-width: 500px;
      p {
        padding: 5px 10px;
        border-radius: 5px;
      }
      & .is-white {
        background: #f6f6f6;
      }
      & .is-red {
        background: #ffeef0;
        & .is-dark {
          color: #ff6f6f;
        }
      }
      & .is-green {
        background: #e6ffed;
        & .is-dark {
          color: #3eb93e;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-analytic/features/logs/pages/user/PageLogsUser.vue?2939d9d6